/* 202 means accepted for processing, never confirmed marketing consent.
   Identity lives in memory only. Skip/close never commits or cancels email.

   Request states (data-email-state / data-sms-state on <olas-signup>):
     idle → submitting → accepted   Klaviyo answered 202: accepted for processing, not a confirmed opt-in.
                       → rejected   client validation or consent (no request), or a 4xx/429 answer.
                       → unknown    timeout, 5xx, or a transport failure while Klaviyo is still reachable:
                                    the request may have been processed, so nothing else is sent.
                       → unsent     Klaviyo is unreachable from this browser and the fallback cannot run.
                       → fallback   Shopify's customer form is being submitted instead (email only).
   The fallback runs only when all of these hold, at most once per page:
     1. the email request failed in transport (TypeError), not with an answer and not by the timeout;
     2. a side-effect-free GET to the same Klaviyo URL also fails in transport, so the host is
        unreachable (blocker, DNS, firewall) and the POST cannot have been delivered;
     3. no earlier request for this address on this page ended in an unknown outcome;
     4. the browser is not offline, and the native form is present;
     5. Shopify's CAPTCHA, when the store has it, has bound the form; it is then submitted with
        requestSubmit(), so validation and every submit listener, including the CAPTCHA's, run.
   Retries are never automatic; a visitor's retry goes to Klaviyo again, which keeps one profile. */
const emailRequests = new Map();
const acceptedEmails = new Set();
const uncertainEmails = new Set();
const pageStates = new Map();
const explicitVariants = new Map();
const revision = '2026-07-15';
const consentVersion = '2026-09-26';
const formVersion = '2026-09-14';
const fallbackKey = 'olas_registry_fallback';
const fallbackMaxAge = 10 * 60 * 1000;
let fallbackStarted = false;
const timing = { request: 15000, probe: 5000, captcha: 10000, navigation: 20000 };

document.addEventListener('change', event => {
  if (!event.isTrusted) return;
  const picker = event.target.closest?.('variant-radios, variant-selects');
  if (picker?.dataset.section && picker.currentVariant) {
    explicitVariants.set(picker.dataset.section, picker.currentVariant);
  }
});

export function filteredAttribution(search, pathname) {
  const params = new URLSearchParams(search);
  const properties = {};
  const source = params.get('utm_source')?.toLowerCase();
  properties.olas_source = ['instagram', 'facebook', 'meta', 'tiktok', 'google', 'email', 'newsletter'].includes(source) ? source : source ? 'other' : 'direct';
  if (/^\/(?:$|(?:pages|products|collections)\/[a-z0-9-]{1,80}\/?$)/i.test(pathname)) properties.olas_page_path = pathname;
  const entry = params.get('entry_point');
  if (['announcement', 'navigation', 'studio', 'process', 'faq', 'search_empty'].includes(entry)) properties.olas_entry_point = entry;
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
    const value = params.get(key);
    if (value && /^[a-z][a-z0-9_-]{0,63}$/i.test(value) && !/\d{7}|email|phone|customer|contact/i.test(value)) properties[`olas_${key}`] = value.toLowerCase();
  }
  return properties;
}

export function subscriptionPayload({ channel, email, phone, list, properties, source }) {
  if (!['email', 'sms'].includes(channel)) throw new Error('Unsupported channel');
  return { data: { type: 'subscription', attributes: {
    custom_source: source,
    profile: { data: { type: 'profile', attributes: {
      email, ...(channel === 'sms' ? { phone_number: phone } : {}), properties,
      subscriptions: { [channel]: { marketing: { consent: 'SUBSCRIBED' } } },
    } } },
  }, relationships: { list: { data: { type: 'list', id: list } } } } };
}

// This validates NANP syntax only. The provider must validate supported US numbers.
export function normalizePhone(value) {
  if (/[^\d+() .-]/.test(value)) return null;
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits[0] === '1') digits = digits.slice(1);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits) ? `+1${digits}` : null;
}

const endpoint = site => `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(site)}`;

function requestError(reason, outcome, causeType) {
  const error = new Error(reason);
  error.outcome = outcome;
  error.causeType = causeType;
  return error;
}

async function sendSubscription(site, payload) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timing.request);
  let response;
  try {
    response = await fetch(endpoint(site), {
      method: 'POST', mode: 'cors', credentials: 'omit', keepalive: true,
      headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision },
      body: JSON.stringify(payload), signal: controller.signal,
    });
  } catch (error) {
    // Browser transport errors contain no profile data; never log request bodies.
    console.warn('[OLAS Registry] Transport error:', error.name, error.message);
    // The keepalive request may still be processed after the abort.
    if (controller.signal.aborted) throw requestError('unknown', 'unknown', 'timeout');
    // A TypeError is either "never left the browser" or "the answer was lost"; checked later.
    throw requestError('unknown', error.name === 'TypeError' ? 'transport' : 'unknown', error.name === 'TypeError' ? 'network' : 'unexpected');
  } finally { clearTimeout(timeout); }
  if (response.status === 202) return;
  if (response.status === 429) throw requestError('limited', 'rejected', 'status');
  if (response.status >= 500) throw requestError('unknown', 'unknown', 'server');
  throw requestError('failed', 'rejected', 'status');
}

// Side-effect-free: a GET the endpoint does not accept, no body, no credentials, opaque answer.
// Reachable (or no answer in time) means the failed POST may have arrived: the outcome is unknown.
async function providerUnreachable(site) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timing.probe);
  try {
    await fetch(endpoint(site), { method: 'GET', mode: 'no-cors', credentials: 'omit', cache: 'no-store', signal: controller.signal });
    return false;
  } catch (error) {
    return !controller.signal.aborted && error.name === 'TypeError';
  } finally { clearTimeout(timeout); }
}

// Shopify's CAPTCHA bootstrap blocks the submit of an unbound customer form and binds a form when
// a visitor focuses it. The fallback form was never focused, so it is bound the same way first.
function bindCaptcha(form) {
  if (!window.Shopify?.captcha) return Promise.resolve(true); // The store does not protect forms.
  const bound = () => Boolean(form.dataset.hcaptchaBound || form.dataset.recaptchaBound);
  if (!bound()) form.querySelector('[type="email"]').dispatchEvent(new FocusEvent('focusin', { bubbles: true }));
  if (!form.dataset.cptcha && !bound()) return Promise.resolve(true); // Not a protected form.
  let waited = 0;
  return new Promise(resolve => {
    const check = () => {
      if (bound()) resolve(true);
      else if (waited >= timing.captcha) resolve(false);
      else { waited += 100; setTimeout(check, 100); }
    };
    check();
  });
}

function analyticsAllowed() {
  try { return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()); } catch { return false; }
}

// Only the page Shopify renders after a fallback post knows its result; the marker carries no identity.
function rememberFallback(data) {
  try { sessionStorage.setItem(fallbackKey, JSON.stringify({ at: Date.now(), ...data })); } catch { /* No storage, no event. */ }
}

function takeFallback(now = Date.now()) {
  let marker = null;
  try {
    marker = JSON.parse(sessionStorage.getItem(fallbackKey));
    if (marker) sessionStorage.removeItem(fallbackKey);
  } catch { return null; }
  return marker && Number.isFinite(marker.at) && now >= marker.at && now - marker.at <= fallbackMaxAge ? marker : null;
}

// The Customer Privacy API and analytics initialize asynchronously on a fresh page: wait, bounded.
function publishWhenReady(name, data, attempt = 0) {
  if (window.Shopify?.analytics?.publish && window.Shopify.customerPrivacy) {
    try { if (analyticsAllowed()) window.Shopify.analytics.publish(name, data); } catch { /* Measurement is optional. */ }
    return;
  }
  if (attempt < 19) setTimeout(() => publishWhenReady(name, data, attempt + 1), 500);
}

export class OlasSignup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) { this.restorePageState(); return; }
    this.initialized = true;
    this.emailForm = this.querySelector('[data-olas-signup="email"]');
    this.smsForm = this.querySelector('[data-olas-signup="sms"]');
    this.copy = JSON.parse(this.querySelector('[data-signup-copy]').textContent);
    if (!this.dataset.siteId || !this.dataset.emailList || !window.fetch) return;
    if (this.querySelector('[data-native-success]')) { // Server result, no inferred SMS identity.
      const fallback = takeFallback();
      if (fallback) publishWhenReady('registry_request_accepted', this.eventData('email', { provider: 'shopify', placement: fallback.placement, page_type: fallback.page_type }));
      return;
    }
    const nativeForm = this.emailForm;
    if (nativeForm.querySelector('[data-error]:not([hidden])') && takeFallback()) {
      publishWhenReady('registry_request_error', this.eventData('email', { provider: 'shopify', reason: 'native', stage: 'request', outcome: 'rejected' }));
    }
    const clientForm = document.createElement('form');
    clientForm.id = `${nativeForm.id}-client`;
    clientForm.className = nativeForm.className;
    clientForm.dataset.olasSignup = 'email';
    for (const child of nativeForm.childNodes) clientForm.append(child.cloneNode(true));
    clientForm.querySelectorAll('input[type="hidden"], script, iframe, [name="h-captcha-response"], [name="g-recaptcha-response"]').forEach(node => node.remove());
    clientForm.querySelector('[type="email"]').name = 'email';
    clientForm.querySelector('[type="checkbox"]').name = 'email_consent';
    // Kept detached: if Klaviyo cannot be reached at all (a privacy blocker), the email still goes
    // through Shopify's own customer form, under the rules at the top of this file.
    this.nativeForm = nativeForm;
    this.emailForm = clientForm;
    this.emailForm.noValidate = true;
    this.dataset.emailState = 'idle';
    this.emailForm.addEventListener('submit', event => this.submitEmail(event));
    nativeForm.replaceWith(clientForm);
    this.emailForm.addEventListener('input', () => {
      if (!this.started) { this.started = true; this.emit('registry_form_start', 'email'); }
    });
    if (this.smsForm) {
      this.smsForm.noValidate = true;
      this.smsForm.addEventListener('submit', event => this.submitSms(event));
      this.querySelector('[data-sms-skip]').addEventListener('click', () => {
        if (this.smsBusy) return;
        this.completeSmsStep('skipped');
        this.emit('registry_sms_skipped', 'sms');
        this.focusReceipt();
      });
    }
    this.restorePageState();
  }

  stateKey() { return `${this.dataset.siteId}:${this.dataset.emailList}:${this.dataset.exhibition}`; }

  restorePageState() {
    const state = pageStates.get(this.stateKey());
    if (state && this.emailForm && (!this.emailBusy || state.email === this.email)) this.applyPageState(state);
  }

  applyPageState(state) {
    this.email = state.email;
    this.emailAccepted = true;
    this.dataset.emailState = 'accepted';
    this.emailForm.hidden = true;
    this.querySelector('[data-email-receipt]').hidden = false;
    this.dataset.step = this.smsForm && !state.sms ? 'sms' : 'done';
    this.smsBusy = Boolean(state.smsBusy);
    if (this.smsForm) {
      this.smsForm.hidden = Boolean(state.sms);
      this.busy(this.smsForm, this.smsBusy);
    }
    const receipt = this.querySelector('[data-sms-receipt]');
    if (receipt) receipt.hidden = state.sms !== 'pending';
    this.smsAccepted = state.sms === 'pending';
    if (this.dataset.step === 'done') this.showNext();
  }

  sharePageState(state) {
    pageStates.set(this.stateKey(), state);
    this.applyPageState(state);
    document.querySelectorAll('olas-signup').forEach(instance => {
      if (instance !== this && instance.initialized && instance.stateKey() === this.stateKey()) instance.restorePageState();
    });
  }

  completeSmsStep(status) {
    this.sharePageState({ ...pageStates.get(this.stateKey()), email: this.email, sms: status, smsBusy: false });
  }

  metadata(channel) {
    const properties = {
      olas_form_version: formVersion, olas_placement: this.dataset.placement,
      olas_page_type: this.dataset.pageType, olas_exhibition: this.dataset.exhibition,
      [`olas_${channel}_consent_version`]: consentVersion,
    };
    if (this.dataset.work) properties.olas_work = this.dataset.work;
    if (this.dataset.productId) {
      properties.olas_product_id = this.dataset.productId;
      const selected = explicitVariants.get(this.dataset.productSection);
      properties.olas_variant_id = String(selected?.id || this.dataset.variantId);
      properties.olas_variant_context_source = selected ? 'user' : this.dataset.variantSource;
      if (selected?.options?.length === 1) properties.olas_size_preference = selected.options[0];
    }
    this.requestId ||= crypto.randomUUID();
    properties.olas_request_id = this.requestId;
    Object.assign(properties, filteredAttribution(location.search, location.pathname));
    return properties;
  }

  eventData(channel, extra) {
    return { placement: this.dataset.placement, page_type: this.dataset.pageType, channel, form_version: formVersion, ...extra };
  }

  // Events: registry_form_start, registry_request_attempt (a request leaves for the provider),
  // registry_request_accepted (provider accepted it for processing; never a confirmed opt-in),
  // registry_request_error (reason, stage, outcome), registry_request_fallback_started (the
  // native form is being posted; not a sign-up), registry_sms_skipped. No personal data.
  emit(name, channel, extra) {
    // Measurement is optional and cannot fail a subscription or bypass analytics consent.
    try {
      if (analyticsAllowed()) window.Shopify.analytics?.publish?.(name, this.eventData(channel, extra));
    } catch { /* Marketing consent remains independent from analytics. */ }
  }

  error(form, key, input, detail = {}) {
    const message = form.querySelector('[data-error]');
    message.textContent = this.copy[key] || this.copy.unknown;
    message.hidden = false;
    if (input) { input.setAttribute('aria-invalid', 'true'); if (this.canFocus()) input.focus(); }
    else if (this.canFocus()) { message.tabIndex = -1; message.focus(); }
    // `reason` separates client validation (no request was made) from provider and transport failures.
    const channel = form === this.emailForm ? 'email' : 'sms';
    const outcome = detail.outcome || (input ? 'rejected' : 'unknown');
    this.dataset[`${channel}State`] = outcome;
    this.emit('registry_request_error', channel, { reason: key, stage: input ? 'validation' : 'request', outcome, ...(detail.cause ? { cause: detail.cause } : {}) });
  }

  clearError(form) {
    form.querySelector('[data-error]').hidden = true;
    form.querySelectorAll('[aria-invalid]').forEach(input => input.removeAttribute('aria-invalid'));
  }

  busy(form, value) {
    const button = form.querySelector('[type="submit"]');
    if (!button.dataset.idleLabel) button.dataset.idleLabel = button.textContent;
    button.textContent = value ? this.copy.sending : button.dataset.idleLabel;
    button.disabled = value;
    form.setAttribute('aria-busy', String(value));
    const skip = form.querySelector('[data-sms-skip]');
    if (skip) skip.disabled = value;
  }

  async submitEmail(event) {
    // Only the client form is handled here; the native fallback form's own submit passes untouched.
    if (event.target && event.target !== this.emailForm) return;
    event.preventDefault();
    if (this.emailBusy || this.emailAccepted) return;
    const input = this.emailForm.querySelector('[type="email"]');
    const consent = this.emailForm.querySelector('[type="checkbox"]');
    this.clearError(this.emailForm);
    input.value = input.value.trim();
    if (!input.checkValidity()) return this.error(this.emailForm, 'invalidEmail', input);
    if (!consent.checked) return this.error(this.emailForm, 'emailConsent', consent);
    const email = input.value;
    const key = `${this.dataset.siteId}:${this.dataset.emailList}:${email.toLowerCase()}`;
    this.emailBusy = true;
    this.busy(this.emailForm, true);
    this.dataset.emailState = 'submitting';
    let ownsRequest = false;
    try {
      if (!acceptedEmails.has(key)) {
        if (!emailRequests.has(key)) {
          ownsRequest = true;
          this.emit('registry_request_attempt', 'email', { provider: 'klaviyo' });
          const promise = sendSubscription(this.dataset.siteId, subscriptionPayload({ channel: 'email', email,
            list: this.dataset.emailList, properties: this.metadata('email'),
            source: `OLAS Registry / ${this.dataset.placement} / email / ${consentVersion}`,
          })).then(() => { acceptedEmails.add(key); }, error => {
            if (error.outcome === 'unknown') uncertainEmails.add(key);
            throw error;
          }).finally(() => emailRequests.delete(key));
          emailRequests.set(key, promise);
        }
        await emailRequests.get(key);
      }
      const previous = pageStates.get(this.stateKey());
      this.sharePageState({ email, sms: previous?.email === email ? previous.sms : undefined });
      if (ownsRequest) this.emit('registry_request_accepted', 'email', { provider: 'klaviyo' });
      this.focusReceipt();
    } catch (error) {
      let detail = { outcome: error.outcome === 'rejected' ? 'rejected' : 'unknown', cause: error.causeType };
      let reason = error.message;
      if (error.outcome === 'transport') {
        const result = ownsRequest ? await this.tryFallback(key, email) : 'unknown';
        if (result === 'started') return;
        if (result === 'unknown') uncertainEmails.add(key);
        else { detail = { outcome: 'unsent', cause: result }; reason = 'failed'; }
      }
      this.dataset.requestDiagnostic = detail.outcome === 'unsent' ? detail.cause : error.causeType || error.message;
      // Native form integrations may reset the input while handling their own failure.
      if (!input.value) input.value = email;
      this.error(this.emailForm, reason, undefined, detail);
    }
    finally { if (!this.fallbackActive) { this.emailBusy = false; this.busy(this.emailForm, false); } }
  }

  // Returns 'started', 'unknown' (the POST may have arrived) or why nothing could be sent.
  async tryFallback(key, email) {
    if (!(await providerUnreachable(this.dataset.siteId))) return 'unknown';
    if (uncertainEmails.has(key)) return 'unknown';
    const form = this.nativeForm;
    if (navigator.onLine === false) return 'offline';
    if (!form || fallbackStarted) return 'fallback_unavailable';
    fallbackStarted = true;
    this.fallbackActive = true;
    this.dataset.emailState = 'fallback';
    form.querySelector('[type="email"]').value = email;
    form.querySelector('[type="checkbox"]').checked = true;
    form.hidden = true;
    this.emailForm.after(form);
    if (!(await bindCaptcha(form))) { this.fallbackActive = false; form.remove(); return 'captcha'; }
    rememberFallback({ placement: this.dataset.placement, page_type: this.dataset.pageType });
    this.emit('registry_request_fallback_started', 'email', { provider: 'shopify' });
    this.dataset.requestDiagnostic = 'fallback';
    form.requestSubmit();
    // If no navigation follows (a challenge closed, a blocked post), the visitor is not left waiting.
    setTimeout(() => {
      if (!this.fallbackActive || !this.isConnected) return;
      this.fallbackActive = false;
      this.emailBusy = false;
      this.busy(this.emailForm, false);
      this.error(this.emailForm, 'unknown', undefined, { outcome: 'unknown', cause: 'fallback' });
    }, timing.navigation);
    return 'started';
  }

  async submitSms(event) {
    event.preventDefault();
    if (!this.emailAccepted || this.smsBusy || this.smsAccepted || this.dataset.step !== 'sms') return;
    const input = this.smsForm.querySelector('[type="tel"]');
    const consent = this.smsForm.querySelector('[type="checkbox"]');
    this.clearError(this.smsForm);
    const phone = normalizePhone(input.value.trim());
    if (!phone) return this.error(this.smsForm, 'invalidPhone', input);
    if (!consent.checked) return this.error(this.smsForm, 'smsConsent', consent);
    this.sharePageState({ ...pageStates.get(this.stateKey()), smsBusy: true });
    this.dataset.smsState = 'submitting';
    this.emit('registry_request_attempt', 'sms', { provider: 'klaviyo' });
    try {
      await sendSubscription(this.dataset.siteId, subscriptionPayload({ channel: 'sms', email: this.email, phone,
        list: this.dataset.smsList, properties: this.metadata('sms'),
        source: `OLAS Registry / ${this.dataset.placement} / sms / ${consentVersion}`,
      }));
      this.completeSmsStep('pending');
      this.dataset.smsState = 'accepted';
      const receipt = this.querySelector('[data-sms-receipt]');
      if (this.canFocus()) receipt.focus();
      this.emit('registry_request_accepted', 'sms', { provider: 'klaviyo' });
    } catch (error) {
      // There is no second SMS path: a transport failure is an unknown outcome, never a fallback.
      this.error(this.smsForm, error.message, undefined, { outcome: error.outcome === 'rejected' ? 'rejected' : 'unknown', cause: error.causeType });
    }
    finally { this.sharePageState({ ...pageStates.get(this.stateKey()), smsBusy: false }); }
  }

  showNext() { const next = this.querySelector('[data-next]'); if (next) next.hidden = false; }
  canFocus() {
    const drawer = this.closest('avaliability-notification-drawer');
    return this.isConnected && (!drawer || drawer.hasAttribute('open'));
  }
  focusReceipt() { if (this.canFocus()) this.querySelector('[data-receipt-focus]').focus(); }
  // Deliberately no disconnectedCallback cancellation: drawer moves/closes cannot cancel a request.
}

if (!customElements.get('olas-signup')) customElements.define('olas-signup', OlasSignup);
